import { AnyAction, Reducer, combineReducers } from '@reduxjs/toolkit';
import localForage from 'localforage';
import { PersistConfig, createMigrate, persistReducer } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import { isDevelopment, isSnapshot, isTesting } from 'vet-bones/utils';

import { databasesReducer } from 'src/reducers/databases';
import { dataSourcesReducer } from 'src/reducers/dataSources';
import {
  CURRENT_MIGRATION_VERSION,
  MIGRATION_MANIFEST,
} from 'src/reducers/migrations/constants';
import { notificationsReducer } from 'src/reducers/notifications';
import { projectsReducer } from 'src/reducers/projects';
import { pruneDeletedProjectModels } from 'src/reducers/transforms/pruneDeletedProjectModels';
import { pruneDeletedResources } from 'src/reducers/transforms/pruneDeletedResources';
import { pruneInvalidRelationshipMappings } from 'src/reducers/transforms/pruneInvalidRelationshipMappings';
import { pruneProjects } from 'src/reducers/transforms/pruneProjects';
import { connectionsSlice } from 'src/store/connections/connections.slice';
import { featuresSlice } from 'src/store/features/features.slice';
import { uiSlice } from 'src/store/ui/ui.slice';

const appReducer = combineReducers({
  connections: connectionsSlice.reducer,
  databases: databasesReducer,
  dataSources: dataSourcesReducer,
  features: featuresSlice.reducer,
  notifications: notificationsReducer,
  projects: projectsReducer,
  ui: uiSlice.reducer,
  version: () => process.env.APP_VERSION || '0.0.1',
});

export type AppReducerReduxState = typeof appReducer extends Reducer<
  infer State
>
  ? State
  : never;

export const RESET_STORE = 'RESET_STORE';

const rootReducer = (
  state: AppReducerReduxState | undefined,
  action: AnyAction
) => {
  if (action.type === RESET_STORE) {
    return createInitialState();
  }
  return appReducer(state, action);
};

const LOCAL_DEV_KEY = '__PILOTHOUSE_DEV_STORE__';
const LOCAL_PROD_KEY = '__PILOTHOUSE_STORE__';
export const LOCAL_STORAGE_KEY =
  isDevelopment() || isSnapshot() || isTesting()
    ? LOCAL_DEV_KEY
    : LOCAL_PROD_KEY;

export const getIndexedDbStorage = (dbKey: string) => {
  const db = localForage.createInstance({
    name: dbKey,
  });

  return {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    getItem: db.getItem,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    setItem: db.setItem,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    removeItem: db.removeItem,
  };
};

export const persistConfig: PersistConfig<AppReducerReduxState> = {
  version: CURRENT_MIGRATION_VERSION,
  key: LOCAL_STORAGE_KEY,
  migrate: createMigrate(MIGRATION_MANIFEST as any, {
    debug: isDevelopment(),
  }),
  storage: getIndexedDbStorage(LOCAL_STORAGE_KEY),
  whitelist: ['projects', 'version'],
  transforms: [
    pruneProjects,
    pruneDeletedProjectModels,
    pruneDeletedResources,
    pruneInvalidRelationshipMappings,
  ],
  stateReconciler: autoMergeLevel2,
};

export const persistedReducer = persistReducer(persistConfig, rootReducer);

export type PersistedReducerReduxState =
  typeof persistedReducer extends Reducer<infer State> ? State : never;

// Create initial state by running a nontriggering action through the reducers.
// Each subreducer will return its default state
export const createInitialState = () =>
  appReducer(undefined, {
    type: '',
  });
